import { getTopicsForChecklist } from "@/api";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import { Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ChecklistTopicAccordion } from "./ChecklistTopicAccordion";

interface ChecklistTopicsListProps {
    checklistId: number;
}

/**
 * Displays the list of topics for a checklist.
 * Renders expandable topic accordions.
 * Auto-expands if there's only one topic.
 */
export const ChecklistTopicsList = ({
    checklistId,
}: ChecklistTopicsListProps) => {
    // Track which topics are expanded (empty set = all collapsed)
    const [expandedTopicIds, setExpandedTopicIds] = useState<Set<number>>(
        new Set()
    );

    // Fetch topics list
    const {
        data: topics,
        isPending,
        error,
    } = useQuery({
        queryKey: ["clause-checklist-topics", checklistId],
        queryFn: () => getTopicsForChecklist(checklistId),
    });

    // Auto-expand if there's only one topic
    useEffect(() => {
        if (topics?.length === 1) {
            setExpandedTopicIds(new Set([topics[0].id]));
        }
    }, [topics]);

    const handleToggle = (topicId: number) => {
        setExpandedTopicIds((prev) => {
            const next = new Set(prev);
            if (next.has(topicId)) {
                next.delete(topicId);
            } else {
                next.add(topicId);
            }
            return next;
        });
    };

    if (isPending) {
        return <LoadingIndicator />;
    }

    if (error) {
        return (
            <Typography color="error" variant="body2">
                Failed to load topics. Please try again.
            </Typography>
        );
    }

    const topicCount = topics?.length ?? 0;

    return (
        <Stack>
            {topicCount === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No topics in this checklist.
                </Typography>
            )}
            {topics?.map((topic) => (
                <ChecklistTopicAccordion
                    key={topic.id}
                    checklistId={checklistId}
                    topic={topic}
                    expanded={expandedTopicIds.has(topic.id)}
                    onToggle={() => handleToggle(topic.id)}
                />
            ))}
        </Stack>
    );
};
